import _ from 'lodash';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { isIOS } from 'react-device-detect';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Category, { unknownCategory } from '../../models/Category.jsx';
import { getAllCategoryGroupsHashedByType } from '../../utilities/getCategoryGroupsByType';
import getCategoryById from '../../utilities/getCategoryById';

export default function CategorySelect(props) {
	const { viewOnly, value, categoryProps, isMulti, blank, transactionName, showSubCategories = true, extraOptionGroups, ...selectProps } = props;
	const [menuIsOpen, updateMenuIsOpen] = useState(false);
	const [overlayIsShown, updateOverlayIsShown] = useState(false);
	const [suggestedCategories, updateSuggestedCategories] = useState([]);
	const transactionFinder = useSelector(state => state.dashboard.transactionFinder);
	const selectRef = useRef(null);
	const categoryGroupsByType = getAllCategoryGroupsHashedByType();

	useEffect(() => {
		let cancelled = false;
		const searchEnded = () => {
			cancelled = true;
		};
		if(!transactionName || !menuIsOpen) {
			return searchEnded;
		}

		transactionFinder.searchByQuery(transactionName)
			.then((results) => {
				if(cancelled) {
					console.error('Previous search cancelled!');
					return;
				}
				const categoriesFound = new Set();
				const suggestionsAllowed = 4;
				for(let transaction of results) {
					const categories = transaction.getCategories();
					for(let category of categories) {
						if(category !== null && categoriesFound.size < suggestionsAllowed) {
							categoriesFound.add(category);
						}
					}
					if(categoriesFound.size >= suggestionsAllowed) {
						break;
					}
				}
				console.log('the suggested categories!', categoriesFound);
				updateSuggestedCategories(Array.from(categoriesFound));
			});
		return searchEnded;
	}, [menuIsOpen, transactionName]);

	useEffect(() => {
		if(menuIsOpen) {
			const openTimeout = setTimeout(() => {
				updateOverlayIsShown(true);
			}, 5);
			return () => {
				clearTimeout(openTimeout);
			};
		} else {
			if(overlayIsShown) {
				setTimeout(() => {
					updateOverlayIsShown(false);
				}, 1);
			}
		}
	}, [menuIsOpen]);

	const categoryGroupsByTypeList = useMemo(() => {
		const categoryGroupsByTypeList = [];
		_.each(categoryGroupsByType, (categoryGroups, key) => {
			categoryGroupsByTypeList.push({
				name: Category.typesNames[key],
				categoryGroups: categoryGroups,
			});
		});
		return categoryGroupsByTypeList;
	}, [categoryGroupsByType]);


	const groupStyles = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	};

	const formatGroupLabel = useCallback(data => (
		<div style={groupStyles}>
			<span>{data.label}</span>
		</div>
	), []);

	const styles = useMemo(() => ({
		control: styles => ({ ...styles, backgroundColor: 'white', cursor: 'pointer' }),
		option: (styles, { data, isDisabled, isFocused, isSelected }) => {
			/**
			 * An option's highlight is normally tinted with its own category's colour. One that is not
			 * backed by a category has no colour to tint with, so it falls back to a neutral grey --
			 * not to nothing, which would leave it the one row in the menu that does not react to the
			 * mouse while every row around it does.
			 */
			const focusedBackground = data.category ? data.category.getColorRgba(0.2) : 'rgba(0, 0, 0, .08)';
			const activeBackground = data.category ? data.category.getColorRgba(0.3) : 'rgba(0, 0, 0, .16)';

			return {
				...styles,
				backgroundColor: isDisabled
					? null
					: isSelected
						? 'black'
						: isFocused
							? focusedBackground
							: null,
				color: isDisabled
					? '#ccc'
					: isSelected
						? 'white'
						: data.color,
				/** Every option here is clickable, and an arrow cursor says otherwise. */
				cursor: isDisabled ? 'not-allowed' : 'pointer',

				':active': {
					...styles[':active'],
					backgroundColor: !isDisabled && (isSelected ? data.color : activeBackground),
				},
			};
		},
		/** Only used by a picker given menuPortalTarget, whose menu lives outside any stylesheet's reach. */
		menuPortal: styles => ({ ...styles, zIndex: 9999 }),
		// input: styles => ({ ...styles, ...dot() }),
		// placeholder: styles => ({ ...styles, ...dot() }),
		// singleValue: (styles, { data }) => ({ ...styles, ...dot(data.color) }),
	}), []);


	const { options, selectedOptions } = useMemo(() => {
		const options = [];
		let selectedOptions = [];
		let valueMap = {};
		if(Array.isArray(value)) {
			for(let innerValue of value) {
				valueMap[innerValue] = innerValue;
			}
		} else {
			valueMap[value] = value;
		}
		if(suggestedCategories.length > 0) {
			const currentCategoryGroup = {
				label: 'Suggestions',
				options: []
			};
			for(let categoryId of suggestedCategories) {
				const category = getCategoryById(categoryId);
				if(!category || category.deleted) {
					continue;
				}
				const categoryData = {
					label: category.fullName(), value: category.id, color: category.getColor(), category: category,
				};
				currentCategoryGroup.options.push(categoryData);
			}
			options.push(currentCategoryGroup);
		}
		const currentCategoryGroup = {
			label: blank || 'Unknown',
			options: [{
				label: blank || 'Unknown',
				value: unknownCategory.id,
				color: 'Black',
				category: unknownCategory
			}],
		};
		const blankCategoryGroup = currentCategoryGroup;
		options.push(currentCategoryGroup);
		/**
		 * Choices that are not a category of this budget at all. Nothing passes these by default, so
		 * the transaction screens are unaffected; the import mapper uses them to say "take the bank
		 * record but no transaction" and "leave these rows out", neither of which a category can say.
		 * They carry no Category, which is why the option styling above has to tolerate its absence.
		 */
		(extraOptionGroups || []).forEach((extraGroup) => {
			options.push(extraGroup);
			extraGroup.options.forEach((option) => {
				if(valueMap[option.value] !== undefined) {
					selectedOptions.push(option);
				}
			});
		});
		categoryGroupsByTypeList.forEach((categoryGroups, key) => {
			categoryGroups.categoryGroups.forEach((categoryGroup) => {
				const currentCategoryGroup = {
					label: categoryGroup.name,
					options: []
				};
				categoryGroup.getCategories().forEach((category) => {
					if(category.deleted) {
						return;
					}
					if(!showSubCategories && category.parent_category_id) {
						return;
					}
					const categoryData = {
						label: category.fullName(), value: category.id, color: category.getColor(), category: category,
					};
					currentCategoryGroup.options.push(categoryData);
					if(valueMap[category.id]) {
						selectedOptions.push(categoryData);
					}
				});
				options.push(currentCategoryGroup);
			});
		});
		if(value === 0)
		{
			selectedOptions.push(blankCategoryGroup);
		}
		return { options, selectedOptions };
	}, [value, suggestedCategories, extraOptionGroups]);

	const menuOpened = useCallback(() => {
		updateMenuIsOpen(true);
	}, []);

	const menuClosed = useCallback(() => {
		updateMenuIsOpen(false);
	}, []);

	const overlayClick = useCallback((e) => {
		updateMenuIsOpen(false);
		e.stopPropagation();
	}, []);

	const onClick = useCallback((e) => {
		e.stopPropagation();
	}, []);

	if(viewOnly) {
		const category = getCategoryById(value);
		return (
			<span>{category ? category.fullName() : 'Unknown'}</span>
		);
	}

	if(!_.size(categoryGroupsByType)) {
		return null;
	}

	return (
		<div
			className={`${(isIOS) && menuIsOpen ? 'squish_view' : null}`}
			onClick={onClick}
		>
			<div
				onClick={overlayClick}
				className={`menu_overlay ${overlayIsShown ? 'active' : ''}`} />
			<Select
				ref={selectRef}
				onMenuOpen={menuOpened}
				onMenuClose={menuClosed}
				defaultMenuIsOpen={false}
				menuIsOpen={menuIsOpen}
				className='browser-default category_select'
				classNamePrefix='category_select'
				value={selectedOptions}
				onClick={onClick}
				options={options}
				formatGroupLabel={formatGroupLabel}
				styles={styles}
				placeholder={blank || (isMulti ? 'Select Categories...' : 'Select Category')}
				isMulti={isMulti}
				{...selectProps}
			/>
		</div>
	);
}